// src/components/admin/Products.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaEdit, FaTrash, FaEye, FaSearch, FaSync, FaBoxOpen, FaCheckCircle, FaFileAlt, FaExclamationTriangle, FaPlus, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './Products.css';

const PRODUCT_LIMIT = 25;
const PAGE_SIZE = 10;

const formatCategory = (slug) =>
  String(slug || '')
    .split('_')
    .map((word) =>
      word.split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('-')
    )
    .join(' ');

const Products = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get('filter') === 'low_stock' ? 'low_stock' : 'all'
  );
  const [lowStockOnly, setLowStockOnly] = useState(searchParams.get('filter') === 'low_stock');

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      // CHANGED: uses admin endpoint that returns ALL products (active + draft)
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/get-all-products.php');
      const data = await response.json();
      console.log('Products fetched:', data);
      if (data.success) {
        setProducts(data.products);
      } else {
        toast.error(data.message || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('Fetch products error:', error);
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    navigate(`/admin/edit-product/${id}`);
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      setDeletingId(id);
      try {
        const response = await fetch('https://demo-site.storii.com.pk/demo-api/admin/delete-product.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(id) })
        });

        const data = await response.json();

        if (data.success) {
          toast.success(`"${name}" deleted successfully!`);
          setProducts(prev => prev.filter(p => p.id !== id));
          await fetchProducts();
        } else {
          toast.error(data.message || 'Failed to delete product');
        }
      } catch (error) {
        console.error('Delete error:', error);
        toast.error('Connection error. Please try again.');
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleView = (id) => {
    window.open(`/product/${id}`, '_blank');
  };

  // ===== REAL STATS NOW =====
  const totalProducts = products.length;
  const outOfStockCount = products.filter(p => !p.stock || p.stock <= 0).length;
  // REAL: uses actual status field from DB
  const activeCount = products.filter(p =>
    p.status !== 'draft' && p.status !== 'inactive' && Number(p.stock) > 0
  ).length;
  const draftCount = products.filter(p => p.status === 'draft').length;

  const usagePercent = Math.min(100, Math.round((totalProducts / PRODUCT_LIMIT) * 100));

  const statCards = [
    { title: 'Total Products', value: totalProducts, sub: 'Products added', icon: <FaBoxOpen />, bg: '#dbeafe', color: '#1d4ed8' },
    { title: 'Active Products', value: activeCount, sub: 'Visible on store', icon: <FaCheckCircle />, bg: '#dcf5e3', color: '#1e8a4c' },
    { title: 'Draft Products', value: draftCount, sub: 'Not visible yet', icon: <FaFileAlt />, bg: '#e5e0f5', color: '#6d4fc7' },
    { title: 'Out of Stock', value: outOfStockCount, sub: 'Needs restock', icon: <FaExclamationTriangle />, bg: '#fbdede', color: '#c8342a' },
  ];

  const categories = useMemo(() => {
    const set = new Set(products.map(p => p.category).filter(Boolean));
    return Array.from(set);
  }, [products]);

  // REAL: status now comes from DB
  const getStatus = (product) => {
    // Draft always wins — drafts are hidden regardless of stock
    if (product.status === 'draft') return 'draft';
    if (product.status === 'inactive') return 'inactive';
    // If stock is 0, treat it as out of stock even if DB status is stale
    if (Number(product.stock) <= 0) return 'out_of_stock';
    if (product.status === 'out_of_stock') return 'out_of_stock';
    return 'active';
  };

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !term ||
        (product.name || '').toLowerCase().includes(term) ||
        (product.category || '').toLowerCase().includes(term) ||
        formatCategory(product.category).toLowerCase().includes(term);

      const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;

      const matchesStatus =
        statusFilter === 'all'
          ? true
          : statusFilter === 'low_stock'
            ? (Number(product.stock) > 0 && Number(product.stock) <= Number(product.low_stock_threshold || 5))
            : getStatus(product) === statusFilter;

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [products, searchTerm, categoryFilter, statusFilter]);

  // Pagination slice
  const totalMatching = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalMatching / PAGE_SIZE));
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    // Optional: scroll to top of table when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setStatusFilter('all');
    setLowStockOnly(false);
    setCurrentPage(1);
  };

  // Reset pagination to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, statusFilter]);

  // Status pill styling helper
  const getStatusPill = (status) => {
    switch (status) {
      case 'active':
        return { className: 'status-active', label: 'Active' };
      case 'draft':
        return { className: 'status-draft', label: 'Draft' };
      case 'inactive':
        return { className: 'status-inactive', label: 'Inactive' };
      case 'out_of_stock':
        return { className: 'status-out', label: 'Out of Stock' };
      default:
        return { className: 'status-active', label: 'Active' };
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="products-admin">
      <div className="products-header">
        <div>
          <h1>Products</h1>
          <p className="products-subtitle">Add, edit, and manage the products shown on your online store.</p>
        </div>
        <button onClick={() => navigate('/admin/add-product')} className="add-product-btn">
          <FaPlus /> Add Product
        </button>
      </div>

      {/* PLAN USAGE */}
      <div className="plan-usage-card">
        <div className="plan-usage-top">
          <div>
            <span className="plan-usage-title">Products Used: {totalProducts} / {PRODUCT_LIMIT}</span>
            <p className="plan-usage-sub">Your current plan allows up to {PRODUCT_LIMIT} products.</p>
          </div>
          <span className="plan-usage-percent">{usagePercent}% used</span>
        </div>
        <div className="plan-usage-track">
          <div className="plan-usage-fill" style={{ width: `${usagePercent}%` }}></div>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="products-stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className="products-stat-card">
            <div className="products-stat-icon" style={{ background: stat.bg, color: stat.color }}>
              {stat.icon}
            </div>
            <p className="products-stat-title">{stat.title}</p>
            <h3 className="products-stat-value">{stat.value}</h3>
            <p className="products-stat-sub">{stat.sub}</p>
          </div>
        ))}
      </div>

      {/* SEARCH + FILTERS */}
      <div className="products-filter-bar">
        <div className="products-search">
          <FaSearch className="products-search-icon" />
          <input
            type="text"
            placeholder="Search by product name or category"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="products-filter-select">
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{formatCategory(cat)}</option>
          ))}
        </select>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="products-filter-select">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          {/* 
          <option value="inactive">Inactive</option>
          */}
          <option value="out_of_stock">Out of Stock</option>
          <option value="low_stock">Low Stock</option>
        </select>

        <button className="reset-filters-btn" onClick={handleResetFilters}>
          <FaSync /> Reset Filters
        </button>
      </div>

      {/* TABLE */}
      <div className="products-table-container">
        {filteredProducts.length === 0 ? (
          <div className="no-products">
            <p>No products found.</p>
          </div>
        ) : (
          <table className="products-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Price</th>
                <th>Sale Price</th>
                {/* 
                <th>Stock</th>
                <th>Threshold</th>
                */}
                <th>Display</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.map(product => {
                const status = getStatus(product);
                const pill = getStatusPill(status);
                return (
                  <tr key={product.id}>
                    <td>
                      <div className="product-cell">
                        <img
                          src={product.image || 'https://via.placeholder.com/50'}
                          alt={product.name}
                          className="product-thumb"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/50';
                          }}
                        />
                        <span>{product.name}</span>
                      </div>
                    </td>
                    <td className="muted-cell">{formatCategory(product.category)}</td>
                    <td>PKR {parseFloat(product.price).toLocaleString()}</td>
                    <td className="muted-cell">
                      {product.sale_price ? `PKR ${parseFloat(product.sale_price).toLocaleString()}` : '—'}
                    </td>
                    {/* 
                    <td>{product.stock}</td>
                    <td>{product.low_stock_threshold}</td>
                    */}
                    <td>
                      <div className="display-tags">
                        {Number(product.featured) === 1 ? (
                          <span className="tag tag-featured">⭐ Featured</span>
                        ) : (
                          <span className="tag tag-not-featured">Not Featured</span>
                        )}
                        {Number(product.is_new_arrival) === 1 && <span className="tag tag-new">New</span>}
                        {Number(product.is_on_sale) === 1 && <span className="tag tag-sale">Sale</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`status-pill ${pill.className}`}>
                        {pill.label}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="icon-action edit-action"
                        onClick={() => handleEdit(product.id)}
                        title="Edit Product"
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        className="icon-action view-action"
                        onClick={() => handleView(product.id)}
                        title="View Product"
                      >
                        <FaEye /> View
                      </button>
                      <button
                        className="icon-action delete-action"
                        onClick={() => handleDelete(product.id, product.name)}
                        disabled={deletingId === product.id}
                        title="Delete Product"
                      >
                        {deletingId === product.id ? '...' : <FaTrash />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* PAGINATION */}
      {!loading && totalMatching > 0 && (
        <div className="products-pagination">
          <span className="products-pagination-info">
            {'Showing ' + ((currentPage - 1) * PAGE_SIZE + 1) + '–' + Math.min(currentPage * PAGE_SIZE, totalMatching) + ' of ' + totalMatching + ' products'}
          </span>
          <div className="products-pagination-controls">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
              <FaChevronLeft />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .map((p, idx, arr) => (
                <React.Fragment key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && <span className="products-pagination-ellipsis">…</span>}
                  <button
                    className={p === currentPage ? 'products-pagination-page active' : 'products-pagination-page'}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;